import { useEffect, useRef, useState } from 'react'
import {
	CalendarCheck,
	CalendarPlus,
	Check,
	ChevronLeft,
	Lightbulb,
	ShoppingBasket,
} from 'lucide-react'
import { Header } from '@components/Header.tsx'
import { ConfirmDialog } from '@components/ConfirmDialog.tsx'
import { IconToggle } from '@components/IconToggle.tsx'
import { OnlineFieldset } from '@components/OnlineFieldset.tsx'
import { AddToGroceriesDialog } from '@apps/groceries/AddToGroceriesDialog.tsx'
import { AddToPlanDialog } from '@apps/meals/AddToPlanDialog.tsx'
import { dayLabelLine } from '@apps/meals/dates.ts'
import { wakeLockSupported } from '@hooks/useWakeLock.ts'
import { api } from '@api'
import { useRouter } from '@router'
import {
	BATCH_COOK,
	CUISINE,
	EFFORT,
	type MealDate,
	PRIMARY_CARB,
	PRIMARY_PROTEIN,
	RECIPE_TYPE,
	type Recipe,
	type RecipeId,
	RecipeTitle,
	SAUCE_BASE,
	SEASON,
	type SystemTags,
} from '@hewey/shared'
import { ImageUploadControl } from '@components/ImageUploadControl.tsx'
import { useImageSrc } from '@hooks/useImageSrc.ts'
import { useOnline } from '@hooks/useOnline.ts'
import { RecipeImage } from './RecipeImage.tsx'
import {
	type DraftSection,
	fromDrafts,
	SectionListEditor,
	toDrafts,
} from './SectionListEditor.tsx'
import {
	emptyTimingDraft,
	fromTimingDraft,
	type TimingDraft,
	TimingFields,
	toTimingDraft,
} from './TimingFields.tsx'
import { KIND_COLOUR, KIND_INK, tagPills } from './tagSummary.ts'
import styles from './RecipeDetail.module.css'

type Props = {
	recipeId: RecipeId
	recipe: Recipe | null
	loading: boolean
	wakeLock: boolean
	onToggleWakeLock: () => void
	onPatched: (recipe: Recipe) => void
	onDeleted: (id: RecipeId) => void
}

// Order + label for each tag field shown in the override grid. Source of
// truth for the dropdown options is the shared enum constants — adding a
// new value to any enum surfaces it here for free.
const TAG_FIELDS: Array<{
	key: keyof SystemTags
	label: string
	options: readonly string[]
}> = [
	{ key: 'primaryProtein', label: 'Protein', options: PRIMARY_PROTEIN },
	{ key: 'primaryCarb', label: 'Carb', options: PRIMARY_CARB },
	{ key: 'sauceBase', label: 'Sauce', options: SAUCE_BASE },
	{ key: 'cuisine', label: 'Cuisine', options: CUISINE },
	{ key: 'type', label: 'Type', options: RECIPE_TYPE },
	{ key: 'effort', label: 'Effort', options: EFFORT },
	{ key: 'batchCook', label: 'Batch cook', options: BATCH_COOK },
	{ key: 'season', label: 'Season', options: SEASON },
]

const EMPTY_TAGS: SystemTags = {
	primaryProtein: 'unknown',
	primaryCarb: 'unknown',
	sauceBase: 'unknown',
	cuisine: 'other',
	season: 'all_year',
	type: 'unknown',
	effort: 'unknown',
	batchCook: 'unknown',
}

const formatMins = (n: number | null): string | null =>
	n === null || n === 0 ? null : `${n} min`

// How long the button holds its receipt before reverting.
const ADDED_MS = 2500

export const RecipeDetail = ({
	recipeId,
	recipe,
	loading,
	wakeLock,
	onToggleWakeLock,
	onPatched,
	onDeleted,
}: Props) => {
	const { navigate } = useRouter()
	const online = useOnline()
	const [editingTitle, setEditingTitle] = useState(false)
	const [titleDraft, setTitleDraft] = useState(recipe?.title ?? '')
	const inputRef = useRef<HTMLInputElement>(null)
	const [editingTags, setEditingTags] = useState(false)
	const [tagsSaved, setTagsSaved] = useState(false)
	const [editingTimings, setEditingTimings] = useState(false)
	const [timingDraft, setTimingDraft] =
		useState<TimingDraft>(emptyTimingDraft())
	const [editingIngredients, setEditingIngredients] = useState(false)
	const [ingredientDraft, setIngredientDraft] = useState<DraftSection[]>([])
	const [editingInstructions, setEditingInstructions] = useState(false)
	const [instructionDraft, setInstructionDraft] = useState<DraftSection[]>([])
	// Local object-URL shown in the hero while an upload is in flight, so the
	// picked photo appears instantly before the round-trip completes.
	const [previewUrl, setPreviewUrl] = useState<string | null>(null)
	const imageSrc = useImageSrc('recipes', recipeId, recipe?.imageUrl ?? null)
	const [confirmingDelete, setConfirmingDelete] = useState(false)
	const savedTimer = useRef<number | undefined>(undefined)
	const [planOpen, setPlanOpen] = useState(false)
	// The day just planned. There's no plan on this page to show the change,
	// so the button itself carries the receipt for a few seconds.
	const [addedTo, setAddedTo] = useState<MealDate | null>(null)
	const addedTimer = useRef<number | undefined>(undefined)
	const [groceriesOpen, setGroceriesOpen] = useState(false)
	// Same receipt as the plan button, naming the list the items went to.
	const [groceryReceipt, setGroceryReceipt] = useState<string | null>(null)
	const receiptTimer = useRef<number | undefined>(undefined)

	useEffect(() => {
		if (editingTitle) inputRef.current?.focus()
	}, [editingTitle])

	useEffect(() => () => window.clearTimeout(savedTimer.current), [])

	useEffect(() => () => window.clearTimeout(addedTimer.current), [])

	useEffect(() => () => window.clearTimeout(receiptTimer.current), [])

	const backButton = (
		<button
			type="button"
			className={styles.back}
			onClick={() => navigate('/recipes')}
			aria-label="Back to recipes"
		>
			<ChevronLeft size={22} aria-hidden="true" />
		</button>
	)

	const wakeLockButton = wakeLockSupported() ? (
		<IconToggle
			on={wakeLock}
			onChange={onToggleWakeLock}
			icon={Lightbulb}
			ariaLabel="Keep screen on"
		/>
	) : null

	if (!recipe) {
		return (
			<main className={styles.screen}>
				<Header left={backButton} />
				<p style={{ color: 'var(--ink-muted)' }}>
					{loading ? 'Loading…' : 'Recipe not found.'}
				</p>
			</main>
		)
	}

	const saveTitle = async () => {
		const trimmed = titleDraft.trim()
		setEditingTitle(false)
		if (trimmed.length === 0 || trimmed === recipe.title) return
		const parsed = RecipeTitle.safeParse(trimmed)
		if (!parsed.success) return
		const next: Recipe = { ...recipe, title: parsed.data }
		onPatched(next)
		try {
			const res = await api.recipe.update(recipeId, {
				title: parsed.data,
			})
			onPatched(res.recipe)
		} catch {
			onPatched(recipe)
		}
	}

	const updateTag = async <K extends keyof SystemTags>(
		key: K,
		value: SystemTags[K],
	) => {
		const base: SystemTags = recipe.systemTags ?? EMPTY_TAGS
		const nextTags: SystemTags = { ...base, [key]: value }
		const optimistic: Recipe = {
			...recipe,
			systemTags: nextTags,
			taggingStatus: 'tagged',
		}
		onPatched(optimistic)
		try {
			const res = await api.recipe.update(recipeId, {
				systemTags: nextTags,
			})
			onPatched(res.recipe)
			setTagsSaved(true)
			window.clearTimeout(savedTimer.current)
			savedTimer.current = window.setTimeout(
				() => setTagsSaved(false),
				2000,
			)
		} catch {
			onPatched(recipe)
		}
	}

	const saveTimings = async () => {
		const timings = fromTimingDraft(timingDraft)
		setEditingTimings(false)
		const unchanged =
			timings.prepTimeMins === recipe.prepTimeMins &&
			timings.cookTimeMins === recipe.cookTimeMins &&
			timings.servings === recipe.servings
		if (unchanged) return
		const next: Recipe = { ...recipe, ...timings }
		onPatched(next)
		try {
			const res = await api.recipe.update(recipeId, timings)
			onPatched(res.recipe)
		} catch {
			onPatched(recipe)
		}
	}

	const saveIngredients = async () => {
		const clean = fromDrafts(ingredientDraft)
		setEditingIngredients(false)
		// A recipe must keep at least one ingredient — discard an emptied edit.
		if (clean.length === 0) return
		const next: Recipe = { ...recipe, ingredients: clean }
		onPatched(next)
		try {
			const res = await api.recipe.update(recipeId, {
				ingredients: clean,
			})
			onPatched(res.recipe)
		} catch {
			onPatched(recipe)
		}
	}

	const saveInstructions = async () => {
		const clean = fromDrafts(instructionDraft)
		setEditingInstructions(false)
		const next: Recipe = { ...recipe, instructions: clean }
		onPatched(next)
		try {
			const res = await api.recipe.update(recipeId, {
				instructions: clean,
			})
			onPatched(res.recipe)
		} catch {
			onPatched(recipe)
		}
	}

	const onDelete = async () => {
		setConfirmingDelete(false)
		try {
			await api.recipe.delete(recipeId)
			onDeleted(recipeId)
		} catch {
			// Leave it visible on failure — the user can try again.
		}
	}

	const tags = recipe.systemTags
	const pills = tags ? tagPills(tags) : []
	// Scraped pages often publish none of these, so the button has to read as
	// "add" rather than "edit" — otherwise there's nothing on the row to edit.
	const hasTimings =
		recipe.prepTimeMins !== null ||
		recipe.cookTimeMins !== null ||
		recipe.servings !== null

	return (
		<main className={styles.screen}>
			<Header left={backButton} right={wakeLockButton} />

			<div className={styles.hero}>
				<RecipeImage
					src={previewUrl ?? imageSrc}
					seed={recipe.id}
					tags={recipe.systemTags}
					title={recipe.title}
					className={styles.heroImg}
					loading="eager"
				/>
				<ImageUploadControl
					hasImage={recipe.imageUrl !== null}
					onPreview={setPreviewUrl}
					onUpload={async (body) => {
						const res = await api.recipe.uploadImage(
							recipe.id,
							body,
						)
						onPatched(res.recipe)
					}}
					onRemove={async () => {
						const res = await api.recipe.removeImage(recipe.id)
						onPatched(res.recipe)
					}}
				/>
			</div>

			<OnlineFieldset className={styles.body}>
				{editingTitle ? (
					<input
						ref={inputRef}
						className={styles.titleInput}
						value={titleDraft}
						maxLength={200}
						onChange={(e) => setTitleDraft(e.target.value)}
						onBlur={() => void saveTitle()}
						onKeyDown={(e) => {
							if (e.key === 'Enter') void saveTitle()
							if (e.key === 'Escape') {
								setTitleDraft(recipe.title)
								setEditingTitle(false)
							}
						}}
					/>
				) : (
					<h1
						className={styles.title}
						onClick={() => {
							if (!online) return
							setTitleDraft(recipe.title)
							setEditingTitle(true)
						}}
					>
						{recipe.title}
					</h1>
				)}

				<div className={styles.meta}>
					{!editingTimings && (
						<>
							{formatMins(recipe.prepTimeMins) && (
								<span>
									Prep {formatMins(recipe.prepTimeMins)}
								</span>
							)}
							{formatMins(recipe.cookTimeMins) && (
								<span>
									Cook {formatMins(recipe.cookTimeMins)}
								</span>
							)}
							{recipe.servings !== null && (
								<span>Serves {recipe.servings}</span>
							)}
						</>
					)}
					{recipe.sourceUrl && (
						<a
							href={recipe.sourceUrl}
							target="_blank"
							rel="noopener noreferrer"
						>
							Source ↗
						</a>
					)}
					<button
						type="button"
						className={`${styles.editBtn} ${styles.metaEdit}`}
						onClick={() => {
							if (editingTimings) void saveTimings()
							else {
								setTimingDraft(toTimingDraft(recipe))
								setEditingTimings(true)
							}
						}}
					>
						{editingTimings
							? 'Done'
							: hasTimings
								? 'Edit times'
								: 'Add times'}
					</button>
				</div>

				{editingTimings && (
					<TimingFields
						value={timingDraft}
						onChange={setTimingDraft}
					/>
				)}

				<section className={styles.section}>
					<div className={styles.sectionHead}>
						<h2>Ingredients</h2>
						<button
							type="button"
							className={styles.editBtn}
							onClick={() => {
								if (editingIngredients) void saveIngredients()
								else {
									setIngredientDraft(
										toDrafts(recipe.ingredients),
									)
									setEditingIngredients(true)
								}
							}}
						>
							{editingIngredients ? 'Done' : 'Edit'}
						</button>
					</div>
					{editingIngredients ? (
						<SectionListEditor
							itemNoun="ingredient"
							value={ingredientDraft}
							onChange={setIngredientDraft}
							itemPlaceholder="e.g. 2 tbsp olive oil"
							maxItemLength={500}
						/>
					) : (
						recipe.ingredients.map((sec, si) => (
							<div key={si} className={styles.subsection}>
								{sec.heading && (
									<h3 className={styles.subheading}>
										{sec.heading}
									</h3>
								)}
								<ul className={styles.list}>
									{sec.items.map((i, idx) => (
										<li key={idx}>{i}</li>
									))}
								</ul>
							</div>
						))
					)}
				</section>

				<section className={styles.section}>
					<div className={styles.sectionHead}>
						<h2>Instructions</h2>
						<button
							type="button"
							className={styles.editBtn}
							onClick={() => {
								if (editingInstructions) void saveInstructions()
								else {
									setInstructionDraft(
										toDrafts(recipe.instructions),
									)
									setEditingInstructions(true)
								}
							}}
						>
							{editingInstructions
								? 'Done'
								: recipe.instructions.length > 0
									? 'Edit'
									: 'Add'}
						</button>
					</div>
					{editingInstructions ? (
						<SectionListEditor
							itemNoun="step"
							value={instructionDraft}
							onChange={setInstructionDraft}
							itemPlaceholder="Describe a step…"
							ordered
							multiline
							maxItemLength={2000}
						/>
					) : recipe.instructions.length > 0 ? (
						recipe.instructions.map((sec, si) => (
							<div key={si} className={styles.subsection}>
								{sec.heading && (
									<h3 className={styles.subheading}>
										{sec.heading}
									</h3>
								)}
								<ol className={styles.list}>
									{sec.items.map((s, idx) => (
										<li key={idx}>{s}</li>
									))}
								</ol>
							</div>
						))
					) : (
						<p className={styles.tagsEmpty}>No instructions yet.</p>
					)}
				</section>

				<section className={styles.section}>
					<div className={styles.tagsHead}>
						<h2>Tags</h2>
						{recipe.taggingStatus !== 'pending' ? (
							<div className={styles.tagsActions}>
								{editingTags && tagsSaved ? (
									<span className={styles.saved}>
										Saved ✓
									</span>
								) : null}
								<button
									type="button"
									className={styles.editTags}
									onClick={() => setEditingTags((v) => !v)}
								>
									{editingTags ? 'Done' : 'Edit tags'}
								</button>
							</div>
						) : null}
					</div>

					{recipe.taggingStatus === 'pending' ? (
						<p className={styles.tagsEmpty}>Tagging…</p>
					) : editingTags ? (
						<div className={styles.tags}>
							{TAG_FIELDS.map((f) => {
								const value = (tags ?? EMPTY_TAGS)[
									f.key
								] as string
								return (
									<label
										key={f.key}
										className={styles.tagField}
									>
										<span className={styles.tagLabel}>
											{f.label}
										</span>
										<select
											className={styles.tagSelect}
											value={value}
											onChange={(e) =>
												void updateTag(
													f.key,
													e.target
														.value as SystemTags[typeof f.key],
												)
											}
										>
											{f.options.map((opt) => (
												<option key={opt} value={opt}>
													{opt.replace(/_/g, ' ')}
												</option>
											))}
										</select>
									</label>
								)
							})}
						</div>
					) : pills.length > 0 ? (
						<div className={styles.pillRow}>
							{pills.map((p) => (
								<span
									key={p.kind}
									className={styles.pill}
									style={{
										background: KIND_COLOUR[p.kind],
										color: KIND_INK[p.kind],
									}}
								>
									{p.label}
								</span>
							))}
						</div>
					) : (
						<p className={styles.tagsEmpty}>
							No tags yet. Tap “Edit tags” to set them.
						</p>
					)}
				</section>

				<div className={styles.footActions}>
					<button
						type="button"
						className={styles.footAction}
						onClick={() => setPlanOpen(true)}
					>
						{addedTo ? (
							<>
								<CalendarCheck size={16} aria-hidden />
								Added to {dayLabelLine(addedTo)}
							</>
						) : (
							<>
								<CalendarPlus size={16} aria-hidden />
								Add to meal plan
							</>
						)}
					</button>
					<button
						type="button"
						className={styles.footAction}
						onClick={() => setGroceriesOpen(true)}
					>
						{groceryReceipt ? (
							<>
								<Check size={16} aria-hidden />
								{groceryReceipt}
							</>
						) : (
							<>
								<ShoppingBasket size={16} aria-hidden />
								Add to groceries
							</>
						)}
					</button>
					<button
						type="button"
						className={styles.delete}
						onClick={() => setConfirmingDelete(true)}
					>
						Delete recipe
					</button>
				</div>
			</OnlineFieldset>

			{/* Mounted only while open so the recipe page doesn't fetch
			    the meal plan until someone asks to plan this recipe. */}
			{planOpen ? (
				<AddToPlanDialog
					recipe={recipe}
					onClose={() => setPlanOpen(false)}
					onAdded={(date) => {
						setPlanOpen(false)
						setAddedTo(date)
						window.clearTimeout(addedTimer.current)
						addedTimer.current = window.setTimeout(
							() => setAddedTo(null),
							ADDED_MS,
						)
					}}
				/>
			) : null}

			{groceriesOpen ? (
				<AddToGroceriesDialog
					recipe={recipe}
					onClose={() => setGroceriesOpen(false)}
					onAdded={(list, res) => {
						setGroceriesOpen(false)
						const n = res.items.length
						setGroceryReceipt(
							n === 0
								? `Already on the ${list} list`
								: `Added ${n} to the ${list} list`,
						)
						window.clearTimeout(receiptTimer.current)
						receiptTimer.current = window.setTimeout(
							() => setGroceryReceipt(null),
							ADDED_MS,
						)
					}}
				/>
			) : null}

			<ConfirmDialog
				open={confirmingDelete}
				title="Delete recipe"
				body={`This will remove "${recipe.title}" for everyone in your household, along with its photo.`}
				confirmLabel="Delete"
				destructive
				onConfirm={() => void onDelete()}
				onClose={() => setConfirmingDelete(false)}
			/>
		</main>
	)
}
